import React from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';

import TablePagination from '../TablePagination/TablePagination';

TableV2.propTypes = {
    tableLayout: PropTypes.oneOf(['auto', 'fixed']), // if fixed, all columns that do not have the width property have the same width
    data: PropTypes.shape({
        columns: PropTypes.arrayOf(
            PropTypes.shape({
                header: PropTypes.string.isRequired,
                accessor: PropTypes.string.isRequired,
                dividerAfter: PropTypes.bool, // set border right
                type: PropTypes.oneOf(['text']), // not used for now
                width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                truncate: PropTypes.bool,
            }).isRequired
        ),
        rows: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                //[accessor]: PropTypes.string
            }).isRequired
        ),
        pagination: PropTypes.shape({
            page: PropTypes.number.isRequired,
            limit: PropTypes.number.isRequired,
            totalItems: PropTypes.number.isRequired,
        }), // if you don't need pagination don't add pagination property to data
    }).isRequired,
    includePagination: PropTypes.bool, // pagination bar is visible if there are more than one page of results
    onPaginationPreviousClick: PropTypes.func,
    onPaginationNextClick: PropTypes.func,
};
// width + maxWidth + truncate = fixed width with text truncation
// maxWidth + truncate (without width) = flexible width up to maxWidth with truncation
// minWidth works as expected and allows shrinking
// truncate without maxWidth = row stretches to fit full content in one line

function TableV2({
    data,
    tableLayout = 'auto',
    includePagination = false,
    onPaginationNextClick,
    onPaginationPreviousClick,
}) {
    if (!data) return null;

    const noRows = data?.pagination?.page === 1 && data?.rows?.length === 0;
    const shouldShowPagination = includePagination && !noRows;

    return (
        <div>
            <div className="border-t-1 border-b-1 border-neutral-200">
                <div className="overflow-x-auto">
                    <table className="w-full" style={{ tableLayout }}>
                        <colgroup>
                            {data.columns.map(({ accessor, width, minWidth, maxWidth }) => (
                                <col
                                    key={accessor}
                                    style={{
                                        width: width || 'auto',
                                        minWidth: minWidth || 'auto',
                                        maxWidth: maxWidth || 'none',
                                    }}
                                />
                            ))}
                        </colgroup>
                        <thead>
                            <tr className="border-b border-neutral-200">
                                {data.columns.map(
                                    ({ accessor, header, dividerAfter, ...column }) => (
                                        <th
                                            key={accessor}
                                            className={classNames(
                                                'px-4 py-2 text-left font-body-bold text-body-bold-s text-neutral-500 bg-neutral-50 truncate',
                                                dividerAfter && 'border-r-1 border-neutral-200'
                                            )}
                                            style={getCellStyle(column)}
                                        >
                                            {header}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-b border-neutral-200 bg-white">
                                    {data.columns.map(({ accessor, dividerAfter, ...column }) => (
                                        <td
                                            key={accessor}
                                            className={classNames(
                                                'px-4 py-2 font-body text-body-regular-s text-neutral-500 truncate',
                                                dividerAfter && 'border-r-1 border-neutral-200'
                                            )}
                                            style={getCellStyle(column)}
                                        >
                                            {row[accessor]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {shouldShowPagination && (
                <TablePagination
                    data={data.pagination}
                    onPreviousClick={onPaginationPreviousClick}
                    onNextClick={onPaginationNextClick}
                    variant="secondary"
                />
            )}
        </div>
    );
}

function getCellStyle(column) {
    const style = {
        maxWidth: column.maxWidth || undefined,
        width: column.width || undefined,
        minWidth: column.minWidth || undefined,
    };

    if (column.truncate) {
        style.whiteSpace = 'nowrap';
        style.overflow = 'hidden';
        style.textOverflow = 'ellipsis';
    } else {
        style.whiteSpace = 'normal';
        style.wordWrap = 'break-word';
        style.overflow = 'hidden';
    }

    return style;
}

export default TableV2;
